function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useRef } from "react";
import { useSelect, useMultipleSelection } from "downshift";
import composeRefs from "@seznam/compose-react-refs";
import TextInputBase from "../../../private/TextInputBase";
import ArrowDownMd from "../../icons/ArrowDownMd";
import { componentToLabelSize, useAutoId } from "../../../private/helpers";
import Checkbox from "./Checkbox";
import forwardRef from "../../../private/forwardRef";
import { StyledLabel, ChevronWrapper, IconWrapper, DisplayContainer, ComponentContainer } from "./styles";
import { ListboxWrapper, Listbox } from "../../Autocomplete/styles";
import ListboxOption from "../../Autocomplete/ListboxOption";
import usePopper from "../../../hooks/usePopper";
import Portal from "../../../private/Portal";
var defaultSelectedOptionsDisplayText = selectedOptions => {
  if (selectedOptions.length === 1) return selectedOptions[0].label;else return selectedOptions.length + " selected";
};

/**
 * These MultiSelect components provide a means to select one or more options from a collapsible list.
 *
 * @see [Select documentation](https://construct-kit.reainternal.net/components/web/core/select)
 */
var MultiSelect = forwardRef((_ref, externalRef) => {
  var {
    id,
    name,
    label,
    options,
    placeholder = "Please select",
    disabled = false,
    sizeVariant = "large",
    listboxMaxHeight = "15.8125rem",
    mapOptionToListNode,
    onSelectedOptionsChange = () => {},
    defaultSelectedOptions = [],
    hideLabel = false,
    selectedOptions,
    icon,
    renderDisplayText = defaultSelectedOptionsDisplayText,
    helpMessage = "",
    errorMessage = "",
    tooltip,
    tooltipProps,
    dangerouslySetClassNames,
    className,
    style,
    appReferenceId
  } = _ref;
  var autoId = useAutoId(id);
  var triggerRef = useRef(null);
  var translateOptionsToValues = options => options.map(option => option.value);
  var translateValuesToOptions = values => values.reduce((translatedOptions, value) => {
    var option = options.find(option => option.value === value);
    return option ? [...translatedOptions, option] : translatedOptions;
  }, []);
  var {
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems
  } = useMultipleSelection(_extends({
    initialSelectedItems: translateOptionsToValues(defaultSelectedOptions),
    onSelectedItemsChange: _ref2 => {
      var {
        selectedItems
      } = _ref2;
      return selectedItems && onSelectedOptionsChange(translateValuesToOptions(selectedItems));
    }
  }, selectedOptions !== undefined && {
    selectedItems: translateOptionsToValues(selectedOptions)
  }, {
    activeIndex: -1
  }));
  var {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps
  } = useSelect({
    id: autoId,
    selectedItem: null,
    items: translateOptionsToValues(options),
    defaultHighlightedIndex: translateOptionsToValues(options).findIndex(option => selectedItems.includes(option)),
    stateReducer: (state, actionAndChanges) => {
      var {
        changes,
        type
      } = actionAndChanges;
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          return _extends({}, changes, {
            isOpen: true,
            highlightedIndex: state.highlightedIndex
          });
        default:
          break;
      }
      return changes;
    },
    onStateChange: _ref3 => {
      var _triggerRef$current;
      var {
        type,
        selectedItem
      } = _ref3;
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          if (selectedItem) {
            var isAlreadySelected = selectedItems.some(item => item === selectedItem);
            if (isAlreadySelected) removeSelectedItem(selectedItem);else addSelectedItem(selectedItem);
          }
          (_triggerRef$current = triggerRef.current) == null || _triggerRef$current.focus();
          break;
        default:
          break;
      }
    }
  });
  var {
    popperRef,
    popperStyle,
    placement,
    refs
  } = usePopper(_extends({
    mounted: isOpen,
    placement: "bottom-start"
  }, appReferenceId && {
    setWidth: true
  }));
  var [focused, setFocused] = React.useState(false);
  var displayText = selectedItems => {
    if (selectedItems.length === 0) return placeholder;else return selectedItems && renderDisplayText(translateValuesToOptions(selectedItems));
  };
  var renderListBox = () => isOpen ? /*#__PURE__*/React.createElement(ListboxWrapper, {
    ref: popperRef,
    $isPortaled: !!appReferenceId,
    $popperStyle: popperStyle,
    className: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.listboxWrapper
  }, /*#__PURE__*/React.createElement(Listbox, _extends({
    $visible: true,
    $sizeVariant: sizeVariant,
    $listboxMaxHeight: listboxMaxHeight,
    $placement: placement,
    $isErrored: !!errorMessage
  }, getMenuProps({}, {
    suppressRefError: true
  }), {
    className: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.listbox,
    "aria-multiselectable": true
  }), options.map((item, index) => {
    var isSelected = selectedItems.some(selectedItem => selectedItem === item.value);
    return mapOptionToListNode ? (/*#__PURE__*/React.cloneElement(mapOptionToListNode({
      option: item,
      sizeVariant,
      isActive: highlightedIndex === index,
      isSelected
    }), getItemProps({
      item: item.value,
      index,
      key: "" + item.value,
      className: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.listboxOption
    }))) : /*#__PURE__*/React.createElement(ListboxOption, _extends({
      sizeVariant: sizeVariant,
      isActive: highlightedIndex === index,
      key: item.value + "-" + index
    }, getItemProps({
      item: item.value,
      index
    }), {
      className: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.listboxOption,
      "aria-selected": isSelected
    }), /*#__PURE__*/React.createElement(Checkbox, {
      readOnly: true,
      id: "checkbox-item-" + index,
      checked: isSelected,
      isActive: highlightedIndex === index,
      dangerouslySetClassNames: dangerouslySetClassNames,
      sizeVariant: sizeVariant
    }), item.label);
  }))) : /*#__PURE__*/React.createElement(ListboxWrapper, {
    $popperStyle: {},
    className: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.listboxWrapper
  }, /*#__PURE__*/React.createElement(Listbox, _extends({
    $visible: false
  }, getMenuProps({}, {
    suppressRefError: true
  }), {
    className: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.listbox
  })));
  return /*#__PURE__*/React.createElement(ComponentContainer, {
    className: appReferenceId ? "CK__MultiSelect__Root " + (className || "") : className,
    style: style
  }, /*#__PURE__*/React.createElement(StyledLabel, _extends({
    disabled: disabled,
    sizeVariant: componentToLabelSize[sizeVariant],
    hideLabel: hideLabel,
    tooltip: tooltip,
    tooltipProps: tooltipProps,
    className: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.label,
    dangerouslySetClassNames: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.labelClassNames
  }, getLabelProps()), label), /*#__PURE__*/React.createElement("div", {
    className: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.selectContainer
  }, /*#__PURE__*/React.createElement(TextInputBase, {
    dangerouslySetClassNames: {
      helpMessage: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.helpMessage,
      errorMessage: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.errorMessage,
      scrollableArea: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.scrollableArea,
      inputBorder: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.inputBorder
    },
    id: autoId,
    squaredCornersPosition: isOpen ? placement.includes("bottom") ? "bottom" : "top" : "none",
    textInputBorderProps: _extends({}, getToggleButtonProps(_extends({}, getDropdownProps({
      preventKeyAction: isOpen,
      // Workaround because downshift won't respect `as: div`
      ref: composeRefs(refs.reference, externalRef, triggerRef),
      onFocus: () => setFocused(true),
      onBlur: () => setFocused(false),
      disabled
    }))), {
      as: "div",
      $focused: focused || isOpen,
      style: {
        outline: "none",
        width: "100%",
        color: "inherit"
      }
    }),
    beforeNode: icon && /*#__PURE__*/React.createElement(IconWrapper, {
      "aria-hidden": "true",
      $sizeVariant: sizeVariant,
      className: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.iconWrapper,
      $disabled: disabled
    }, icon),
    afterNode: /*#__PURE__*/React.createElement(ChevronWrapper, {
      $sizeVariant: sizeVariant,
      "aria-hidden": "true",
      $isOpen: isOpen,
      $disabled: disabled,
      className: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.chevronWrapper
    }, /*#__PURE__*/React.createElement(ArrowDownMd, null)),
    renderInput: () => /*#__PURE__*/React.createElement(DisplayContainer, {
      $sizeVariant: sizeVariant,
      className: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.displayContainer,
      $disabled: disabled,
      $isPlaceholder: selectedItems.length === 0
    }, /*#__PURE__*/React.createElement("span", {
      className: dangerouslySetClassNames == null ? void 0 : dangerouslySetClassNames.displayText
    }, displayText(selectedItems)), /*#__PURE__*/React.createElement("input", {
      type: "hidden",
      readOnly: true,
      name: name || autoId,
      value: selectedItems || "",
      tabIndex: -1
    })),
    disabled: disabled,
    sizeVariant: sizeVariant,
    helpMessage: helpMessage,
    errorMessage: errorMessage
  }), appReferenceId ? /*#__PURE__*/React.createElement(Portal, {
    id: appReferenceId,
    trigger: refs.reference.current,
    portalElement: refs.floating.current
  }, /*#__PURE__*/React.createElement("span", {
    className: "CK__MultiSelect__OverlayWrapper " + (className || "")
  }, renderListBox())) : /*#__PURE__*/React.createElement(React.Fragment, null, renderListBox())));
});
MultiSelect.displayName = "MultiSelect";
export default MultiSelect;